/**
 * The presets (node templates), the libraries they are kept in, and using one: a preset
 * becomes **one** `graph.add_node` request carrying the node and its auto-links, so a single
 * undo removes everything it created.
 *
 * Presets come from several **sources**, each a library arranged in folders:
 *
 * | Source | Where it is kept | Who may change it |
 * |---|---|---|
 * | `builtin` | the core (`presets.list`), the same in every app | nobody: copy a preset out to change it |
 * | `computer` | the core this page talks to, when it is not a space | whoever uses it |
 * | `personal` | your account on the space's server (`/api/account/presets`) | you |
 * | `team` | the space's team on its server (`/api/teams/<team>/presets`) | its editors and owners |
 *
 * In a space, `personal` and `team` replace `computer` (the space's own core keeps no presets
 * of yours). The libraries on a server are read when the drawer opens, since others may have
 * changed them.
 *
 * Also holds what the browser shows (the drawer open or not, which source and folder) and the
 * preset being edited (`draft`), so the inspector's "Save as preset" can open the form filled.
 */
import { batch, createMemo, createSignal, type Accessor } from "solid-js";
import type { Preset } from "@protocol/Preset";
import type { PresetEdit } from "@protocol/PresetEdit";
import type { PresetLibrary } from "@protocol/PresetLibrary";
import type { SpaceInfo } from "@protocol/SpaceInfo";
import { isClientError, type Client } from "../client/client";
import type { Actions } from "../client/actions";
import type { AccountsApi } from "../client/accounts";
import type { SpaceLink } from "../client/space";
import type { CommandSet } from "../shared/commands";
import type { MenuSection, ToolbarButton } from "../shared/menus";
import type { PresetDraft } from "../shared/drafts";
import { copyInto } from "../shared/presetTree";
import { toast } from "../shared/toast";
import type { Spot } from "../shared/spot";

/** Which library a preset is in. */
export type SourceId = "builtin" | "computer" | "personal" | "team";

/** One library as the browser shows it. */
export interface PresetSource {
  /** Which one. */
  id: SourceId;
  /** Its name in the browser ("Built-in", "This computer", "My presets", the team's name). */
  label: string;
  /** Whether it can be changed here; {@link PresetSource.why} says why not. */
  readOnly: boolean;
  /** Why it is read-only. */
  why: string;
  /** Its folders and presets. */
  library: PresetLibrary;
}

/** Where the browser is: a source and a folder in it (`""`: its top). */
export interface PresetPlace {
  /** The source. */
  source: SourceId;
  /** The folder. */
  folder: string;
}

/** The preset in the form, and the source it will be saved to. */
export interface PresetDraftPlace {
  /** The preset (empty `id`: a new one). */
  preset: PresetDraft;
  /** Where it is saved. */
  source: SourceId;
}

/** The presets and what can be done with them. */
export interface Presets {
  /** Every source shown, in order: built-in first. */
  sources: Accessor<PresetSource[]>;
  /** One source (`undefined` when it is not shown here). */
  source(id: SourceId): PresetSource | undefined;
  /** The sources that can be changed (where a preset can be saved). */
  writable: Accessor<PresetSource[]>;
  /** Where the browser is. */
  place: Accessor<PresetPlace>;
  /** Goes to a folder of a source. */
  go(source: SourceId, folder: string): void;
  /** Whether the drawer is open. */
  drawer: Accessor<boolean>;
  /** Opens or closes the drawer (opening reads the server's libraries again). */
  setDrawer(open: boolean): void;
  /** The preset shown in the edit form, or `null`. */
  draft: Accessor<PresetDraftPlace | null>;
  /**
   * Shows `draft` in the form (`null` closes it), to be saved to `source` (by default the one
   * browsed when it can be changed, else the first that can).
   */
  setDraft(draft: PresetDraft | null, source?: SourceId): void;
  /** Applies one edit to a source. Resolves `true` on success (a refusal is an error toast). */
  edit(source: SourceId, edit: PresetEdit): Promise<boolean>;
  /** Copies a preset into a folder of another source (its id kept when free there). */
  copy(preset: Preset, to: SourceId, folder: string): Promise<boolean>;
  /**
   * Creates a node from `preset` with its auto-links, in one undoable edit. `place` is called
   * with the node's name just before the request, to choose where it appears on the graph;
   * `spot`: where it goes in the view shown (a card dropped on the diagram or the board).
   * Resolves with the new node's id.
   */
  apply(preset: Preset, place?: (name: string) => void, spot?: Spot | null): Promise<string | null>;
}

/** What {@link createPresets} needs. */
export interface PresetsDeps {
  /** The core this page talks to. */
  client: Client;
  /** Edits of the graph. */
  actions: Actions;
  /** The space this page works in (`null`: none), and whether its core is on this computer. */
  spaces: { current: SpaceLink | null; onThisComputer: boolean };
  /** Calls to a server's accounts API as this browser's session there (see `state/accounts.ts`). */
  accounts: { call<T>(server: string, call: (api: AccountsApi) => Promise<T>, quiet?: boolean): Promise<T | null> };
}

/** An empty library. */
const EMPTY: PresetLibrary = { folders: [], presets: [] };

/** Starts mirroring the presets. Call inside a Solid root. */
export function createPresets({ client, actions, spaces, accounts }: PresetsDeps): Presets {
  const [builtin, setBuiltin] = createSignal<PresetLibrary>(EMPTY);
  const [computer, setComputer] = createSignal<PresetLibrary>(EMPTY);
  const [personal, setPersonal] = createSignal<PresetLibrary | null>(null);
  const [team, setTeam] = createSignal<PresetLibrary | null>(null);
  const [info, setInfo] = createSignal<SpaceInfo | null>(null);
  const [place, setPlace] = createSignal<PresetPlace>({ source: "builtin", folder: "" });
  const [drawer, setDrawerOpen] = createSignal(false);
  const [draft, setDraftPlace] = createSignal<PresetDraftPlace | null>(null);
  const space = spaces.current;

  const sources = createMemo<PresetSource[]>(() => {
    const list: PresetSource[] = [{ id: "builtin", label: "Built-in", readOnly: true, why: "Built-in presets cannot be changed: copy one to another library to change it.", library: builtin() }];
    if (!space) {
      list.push({ id: "computer", label: spaces.onThisComputer ? "This computer" : "This server", readOnly: false, why: "", library: computer() });
      return list;
    }
    const mine = personal();
    if (mine) list.push({ id: "personal", label: "My presets", readOnly: false, why: "", library: mine });
    const theirs = team();
    const about = info();
    if (theirs && about) {
      const readOnly = about.team.role === "viewer" || about.team.role === "commenter";
      list.push({ id: "team", label: about.team.name, readOnly, why: readOnly ? `You are a ${about.team.role} in ${about.team.name}: its editors and owners change its presets.` : "", library: theirs });
    }
    return list;
  });
  const source = (id: SourceId) => sources().find((s) => s.id === id);
  const writable = createMemo(() => sources().filter((s) => !s.readOnly));

  /** Asks the core for its libraries (on every (re)connection). */
  const load = async () => {
    try {
      const response = await client.request({ method: "presets.list" });
      if (response.type === "presets") batch(() => (setBuiltin(response.builtin), setComputer(response.library)));
    } catch {
      // Disconnected; the next onOpen loads it.
    }
  };
  client.onOpen(() => void load());
  client.onEvent((event) => {
    if (event.event === "presets.changed") setComputer(event.data.library);
  });

  /** Reads your library and the team's from the space's server (quietly: the space works without them). */
  const loadServer = async () => {
    if (!space) return;
    const about = info() ?? (await accounts.call(space.server, (a) => a.space(space.space), true));
    if (!about) return;
    setInfo(about);
    const [mine, theirs] = await Promise.all([accounts.call(space.server, (a) => a.personalPresets(), true), accounts.call(space.server, (a) => a.teamPresets(about.team.id), true)]);
    batch(() => {
      if (mine) setPersonal(mine);
      if (theirs) setTeam(theirs);
    });
  };
  void loadServer();

  /** Sends an edit to where `id` is kept; gives the library after it (`null`: refused, said in a toast). */
  const send = async (id: SourceId, edit: PresetEdit): Promise<PresetLibrary | null> => {
    if (id === "computer") {
      try {
        const response = await client.request({ method: "presets.edit", params: { edit } });
        return response.type === "presets" ? response.library : null;
      } catch (error) {
        toast(isClientError(error) ? error.message : String(error), "error");
        return null;
      }
    }
    if (!space) return null;
    if (id === "personal") return accounts.call(space.server, (a) => a.editPersonalPresets(edit));
    const teamId = info()?.team.id;
    return teamId ? accounts.call(space.server, (a) => a.editTeamPresets(teamId, edit)) : null;
  };

  /** Applies one edit to a source, keeping its new library. */
  async function edit(id: SourceId, change: PresetEdit): Promise<boolean> {
    const target = source(id);
    if (!target) return false;
    if (target.readOnly) {
      toast(target.why, "error");
      return false;
    }
    const library = await send(id, change);
    if (!library) return false;
    if (id === "computer") setComputer(library);
    else if (id === "personal") setPersonal(library);
    else if (id === "team") setTeam(library);
    return true;
  }

  return {
    sources,
    source,
    writable,
    place: () => (source(place().source) ? place() : { source: "builtin", folder: "" }),
    go: (id, folder) => setPlace({ source: id, folder }),
    drawer,
    setDrawer(open) {
      setDrawerOpen(open);
      if (open) void loadServer();
    },
    draft,
    setDraft(preset, id) {
      if (!preset) return setDraftPlace(null);
      const browsing = source(place().source);
      const to = id ?? (browsing && !browsing.readOnly ? browsing.id : writable()[0]?.id);
      if (!to) return toast("There is no library you may change here.", "error");
      // A new preset goes in the folder being browsed, when it is saved to the source browsed.
      const folder = !preset.id && !preset.folder && to === place().source ? place().folder : preset.folder;
      setDraftPlace({ preset: { ...preset, folder }, source: to });
    },
    edit,
    async copy(preset, to, folder) {
      const target = source(to);
      if (!target) return false;
      const ok = await edit(to, { edit: "save", preset: copyInto(preset, target.library, folder) });
      if (ok) toast(`Copied ${preset.label} to ${target.label}${folder ? ` / ${folder}` : ""}.`);
      return ok;
    },
    async apply(preset, placeIt, spot) {
      const name = preset.name.trim() || preset.label;
      placeIt?.(name);
      const result = await actions.addNode(
        { name, kind: preset.kind, categories: preset.categories, note: preset.note, data: preset.data, ...spot?.place(preset.kind) },
        preset.links.map((l) => ({ kind: l.kind, to: l.target, label: "" })),
        spot?.first,
      );
      if (!result) return null;
      const made = preset.links.length - result.warnings.length;
      let message = `Added ${name}${made > 0 ? ` with ${made} ${made === 1 ? "link" : "links"}` : ""}.`;
      if (result.warnings.length) message += ` ${result.warnings.join(". ")}.`;
      toast(message);
      return result.id;
    },
  };
}

/** The presets' commands: `presets.drawer` opens or closes the drawer (Ctrl+Space). */
export function presetCommands(presets: Presets): CommandSet {
  return { "presets.drawer": () => presets.setDrawer(!presets.drawer()) };
}

/** The Window menu's drawer (like Unreal's content drawer). */
export function presetMenus(presets: Presets): MenuSection[] {
  return [{ menu: "window", section: "Drawers", order: 5, entries: () => [{ label: "Assets drawer", command: "presets.drawer", checked: presets.drawer() }] }];
}

/** The toolbar's presets drawer. */
export function presetToolbar(presets: Presets): ToolbarButton[] {
  return [{ id: "tool-presets", section: "create", order: 30, icon: "presets", label: "Assets", title: "Files and node templates: drag one onto the graph", command: "presets.drawer", pressed: () => presets.drawer() }];
}
